/**
 * Sprites for the profile scene: actors, ornament and headers.
 *
 * These are the things a theme *contains*, as opposed to `patterns.js`, which
 * is the thing a theme is *wallpapered with*. The two are deliberately
 * separate now: tiling stops at rare, and everything above it gets a scene
 * built out of the pieces below instead of a denser stamp.
 *
 * Three roles, and a sprite belongs to exactly one of them:
 *
 *   actor     Moves. Falls and rotates (petals, leaves, motes), drifts across
 *             (a vulture, a cloud), or peeks up from behind the horizon and
 *             hides again (a pig, an invader).
 *   ornament  Doesn't move, and sits *beside the card*, running its full
 *             height — cacti today. Drawn at the card's scale, not the page's.
 *   header    Doesn't move, and sits *on top of the card*, turning the top
 *             edge into architecture — a hung board today. A header can also
 *             be rail-only, with no drawing at all.
 *
 * Authored as raw SVG inner markup rather than bare path strings (which is
 * what patterns.js takes) because these carry internal shading — a pig needs
 * a darker snout, a column needs its flutes — and a single flat silhouette
 * can't.
 *
 * Two kinds live here, and the difference matters when adding one:
 *
 *   tinted    Shapes with no `fill` of their own, taking it from the `<g>`
 *             the renderer wraps them in, so the theme chooses the colour.
 *             Shading is layered on as `#000`/`#fff` at low opacity, which
 *             works over any tint. Most sprites are these.
 *   self-      Full-colour art (the pig and the chicken). Every shape sets
 *   coloured   its own fill, so the wrapping `<g fill>` — and therefore the
 *             `color` in the theme's spec — has no effect. Fine, and the
 *             point for real artwork; just don't expect to retint it.
 *
 * `vb` is the viewBox. Actors are drawn to sit on their own baseline, so a
 * peeker translated to `translateY(100%)` is exactly hidden.
 */
export const SPRITES = Object.freeze({
    // ── actors: peekers ──────────────────────────────────────────────────
    // Pixel art, and unlike the silhouette sprites it carries its own colours
    // — the `<g fill>` the renderer wraps it in is overridden by every rect
    // here, so the `color` in a theme's peek spec has no effect on it. The
    // artboard rect the export includes is dropped: it's opaque white and
    // would render as a box with a pig somewhere inside it.
    pig: {
        vb: '0 0 20 24',
        svg: `<rect x="4" y="10" width="12" height="9" fill="#E86E6E"/>
    <rect x="3" y="8" width="14" height="12" fill="#E86E6E"/>
    <rect width="4" height="4" transform="matrix(1 0 0 -1 4 24)" fill="#FF8686"/>
    <rect width="4" height="4" transform="matrix(1 0 0 -1 12 24)" fill="#FF8686"/>
    <rect x="5" y="11" width="10" height="9" fill="#FF8686"/>
    <rect width="10" height="1" transform="matrix(1 0 0 -1 5 21)" fill="#DB6363"/>
    <rect x="8" y="15" width="4" height="3" fill="#FFB5B5"/>
    <rect x="11" y="16" width="1" height="1" fill="#B75D5D"/>
    <rect x="8" y="16" width="1" height="1" fill="#B75D5D"/>
    <rect x="5" y="13" width="2" height="2" fill="#FFE4E4"/>
    <rect x="5" y="13" width="1" height="2" fill="#232323"/>
    <rect x="13" y="13" width="2" height="2" fill="#FFE4E4"/>
    <rect x="14" y="13" width="1" height="2" fill="#232323"/>`,
    },
    chicken: {
        vb: '0 0 22 24',
        svg: `<rect x="5" y="11" width="12" height="10" fill="#D9D9D9"/>
    <rect width="1" height="5" transform="matrix(-1 0 0 1 18 14)" fill="#E9E9E9"/>
    <rect width="1" height="5" transform="matrix(-1 0 0 1 5 14)" fill="#E9E9E9"/>
    <rect x="7" y="8" width="8" height="7" fill="#EEEEEE"/>
    <rect x="7" y="12" width="8" height="1" fill="#FFB53D"/>
    <rect x="8" y="21" width="1" height="3" fill="#FFB53D"/>
    <rect x="12" y="21" width="1" height="3" fill="#FFB53D"/>
    <rect x="7" y="23" width="7" height="1" fill="#FFB53D"/>
    <rect x="7" y="13" width="8" height="1" fill="#D89323"/>
    <rect x="10" y="14" width="2" height="2" fill="#FF4949"/>
    <rect x="8" y="10" width="1" height="2" fill="#232323"/>
    <rect x="13" y="10" width="1" height="2" fill="#232323"/>`,
    },
    horse: {
        vb: '0 0 20 24',
        svg: `<rect x="5" y="12" width="10" height="6" fill="#53381C"/>
    <rect x="6" y="11" width="8" height="7" fill="#32200D"/>
    <rect width="2" height="6" transform="matrix(1 0 0 -1 5.5 24)" fill="#8B6A4A"/>
    <rect width="2" height="6" transform="matrix(1 0 0 -1 12.5 24)" fill="#8B6A4A"/>
    <rect width="8" height="0.98176" transform="matrix(1 0 0 -1 6 18.9818)" fill="#53381C"/>
    <rect width="3" height="2" transform="matrix(1 0 0 -1 12 24)" fill="#352515"/>
    <rect width="3" height="2" transform="matrix(1 0 0 -1 5 24)" fill="#352515"/>
    <rect x="7.5" y="6.982" width="5" height="8" fill="#8B6A4A"/>
    <rect x="7" y="12.982" width="6" height="2" fill="#8B6A4A"/>
    <rect x="8" y="12.982" width="4" height="3" fill="#523D28"/>
    <rect x="11" y="12.982" width="1" height="1" fill="#232323"/>
    <rect x="8" y="12.982" width="1" height="1" fill="#232323"/>
    <rect x="9" y="3.982" width="2" height="3" fill="#352515"/>
    <rect width="1" height="4" transform="matrix(1 0 0 -1 9.5 10.964)" fill="#6F4E2C"/>
    <rect x="7.5" y="7.982" width="1" height="2" fill="#E6D9D9"/>
    <rect x="7.5" y="8.982" width="1" height="1" fill="#232323"/>
    <rect x="11.5" y="7.982" width="1" height="2" fill="#C4C4C4"/>
    <rect x="11.5" y="8.982" width="1" height="1" fill="#232323"/>
    <rect x="12.5" y="5.964" width="1" height="2.018" fill="#342414"/>
    <rect x="6.5" y="5.964" width="1" height="2.018" fill="#342414"/>`,
    },
    cow: {
        vb: '0 0 22 24',
        svg: `<rect x="4" y="10" width="12" height="9" fill="#E86E6E"/>
    <rect x="3" y="10" width="14" height="10" fill="#332518"/>
    <rect width="4" height="4" transform="matrix(1 0 0 -1 4 24)" fill="#6F5235"/>
    <rect width="4" height="4" transform="matrix(1 0 0 -1 12 24)" fill="#6F5235"/>
    <rect x="6" y="12" width="4" height="3" fill="#89745E"/>
    <rect x="5" y="18" width="10" height="2" fill="#453422"/>
    <rect x="10" y="7" width="4" height="8" fill="#6F5235"/>
    <rect x="6" y="7" width="4" height="5" fill="#6F5235"/>
    <rect x="6" y="13" width="2" height="2" fill="#6F5235"/>
    <rect width="8" height="1" transform="matrix(1 0 0 -1 6 16)" fill="#231608"/>
    <rect x="8" y="11" width="4" height="3" fill="#352515"/>
    <rect x="11" y="12" width="1" height="1" fill="#232323"/>
    <rect x="8" y="12" width="1" height="1" fill="#232323"/>
    <rect x="6" y="8" width="2" height="2" fill="#E6D9D9"/>
    <rect x="6" y="8" width="1" height="2" fill="#232323"/>
    <rect x="12" y="8" width="2" height="2" fill="#C4C4C4"/>
    <rect x="13" y="8" width="1" height="2" fill="#232323"/>
    <rect x="14" y="6" width="1" height="2" fill="#342414"/>
    <rect x="5" y="6" width="1" height="2" fill="#342414"/>`,
    },
    invader: {
        vb: '0 0 24 20',
        svg: `<path d="M6 2h3v3H6ZM15 2h3v3h-3ZM9 5h6v3H9ZM3 8h18v6H3ZM0 11h3v6H0ZM21 11h3v6h-3ZM6 14h3v3H6ZM15 14h3v3h-3ZM9 17h6v3H9Z"/>
    <path d="M7.5 9.5h3v3h-3ZM13.5 9.5h3v3h-3Z" fill="#000" fill-opacity=".55"/>`,
    },

    // ── actors: fallers and drifters ─────────────────────────────────────
    leaf: { vb: '0 0 24 24', svg: '<path d="M20 3C10 3 3 8 3 15a6 6 0 0 0 1.5 4C6 13 10 9 16 7c-4 3-7 6-9 12a6 6 0 0 0 4 1c7 0 9-8 9-17z"/>' },
    // Deliberately a plain circle: a mote is a speck caught in a light shaft,
    // and any silhouette at all reads as debris instead.
    mote: { vb: '0 0 24 24', svg: '<circle cx="12" cy="12" r="9"/>' },
    vulture: { vb: '0 0 32 12', svg: '<path d="M1 8c5-1 7-5 9.5-5S15 7 16 7s3.5-4 5.5-4S26 7 31 8c-5 1-8-2-9.5-2S17 10 16 10s-4-4-5.5-4S6 9 1 8Z"/>' },
    cloud: { vb: '0 0 24 24', svg: '<path d="M7 19.5a5.4 5.4 0 0 1-.5-10.8 7 7 0 0 1 13.3 2.3 4.5 4.5 0 0 1-1.3 8.5z"/>' },

    // ── wild west: drawn art, self-coloured like the farm animals ────────
    cactus: {
        vb: '0 0 20 24',
        svg: `<path d="M8.87175 26.718V22.5811H7.7435C7.00047 22.5811 6.35196 22.2209 5.91155 21.7805C5.47114 21.3401 5.1109 20.6916 5.1109 19.9485V15.4355C5.1109 14.8124 5.61604 14.3073 6.23916 14.3073C6.86227 14.3073 7.36741 14.8124 7.36741 15.4355V19.9485C7.36741 19.9577 7.38326 20.0613 7.50697 20.185C7.63069 20.3088 7.73435 20.3246 7.7435 20.3246H11.1283V26.718C11.1283 27.3412 10.6231 27.8463 10 27.8463C9.37688 27.8463 8.87175 27.3412 8.87175 26.718Z" fill="#678C43"/>
    <path d="M11.1283 23.7094V21.0768H12.2565C13.0194 21.0768 13.6732 20.6889 14.1075 20.2328C14.5452 19.7732 14.889 19.1119 14.8891 18.3693V10.5464C14.8891 9.9233 14.384 9.41816 13.7608 9.41816C13.1377 9.41816 12.6326 9.9233 12.6326 10.5464V18.3693C12.6325 18.4165 12.6001 18.5446 12.4739 18.677C12.3442 18.8132 12.2457 18.8203 12.2565 18.8203H8.87175V23.7094C8.87175 24.3325 9.37689 24.8376 10 24.8376C10.6231 24.8376 11.1283 24.3325 11.1283 23.7094Z" fill="#678C43"/>
    <path d="M8.87175 25.2137V8.66599C8.87175 8.04287 9.37689 7.53774 10 7.53774C10.6231 7.53774 11.1283 8.04287 11.1283 8.66599V25.2137C11.1283 25.8368 10.6231 26.342 10 26.342C9.37689 26.342 8.87175 25.8368 8.87175 25.2137Z" fill="#678C43"/>`,
    },
    cactusTall: {
        vb: '0 0 20 24',
        svg: `<path d="M8.85573 22.3043V18.1087H7.71146C6.95788 18.1087 6.30017 17.7433 5.85351 17.2966C5.40684 16.85 5.04149 16.1923 5.04149 15.4387V10.8616C5.04149 10.2296 5.5538 9.71733 6.18576 9.71733C6.81773 9.71733 7.33003 10.2296 7.33003 10.8616V15.4387C7.33003 15.448 7.3461 15.5531 7.47158 15.6786C7.59705 15.804 7.70219 15.8201 7.71146 15.8201H11.1443V22.3043C11.1443 22.9363 10.632 23.4486 10 23.4486C9.36804 23.4486 8.85573 22.9363 8.85573 22.3043Z" fill="#678C43"/>
    <path d="M11.1443 24.5929V21.9229H12.2885C13.0623 21.9229 13.7253 21.5295 14.1659 21.0669C14.6098 20.6008 14.9584 19.9302 14.9585 19.1769V11.243C14.9585 10.6111 14.4462 10.0988 13.8142 10.0988C13.1823 10.0988 12.67 10.6111 12.67 11.243V19.1769C12.6698 19.2248 12.637 19.3548 12.5091 19.4891C12.3775 19.6272 12.2776 19.6344 12.2885 19.6344H8.85573V24.5929C8.85573 25.2248 9.36804 25.7371 10 25.7371C10.632 25.7371 11.1443 25.2248 11.1443 24.5929Z" fill="#678C43"/>
    <path d="M8.85573 25.3557V8.57306C8.85573 7.9411 9.36804 7.42879 10 7.42879C10.632 7.42879 11.1443 7.9411 11.1443 8.57306V25.3557C11.1443 25.9877 10.632 26.5 10 26.5C9.36804 26.5 8.85573 25.9877 8.85573 25.3557Z" fill="#678C43"/>`,
    },
    snake: {
        vb: '0 0 20 24',
        svg: `<rect x="10.5764" y="8.87969" width="0.449188" height="1.29412" fill="#EC4043" stroke="#EC4043" stroke-width="0.2"/>
    <rect x="-0.0323833" y="0.137664" width="0.358324" height="0.78435" transform="matrix(0.850236 -0.526402 -0.526402 -0.850236 10.744 9.3007)" fill="#EC4043" stroke="#EC4043" stroke-width="0.2"/>
    <rect x="10.9579" y="9.2007" width="0.358324" height="0.78435" transform="rotate(-148.237 10.9579 9.2007)" fill="#EC4043" stroke="#EC4043" stroke-width="0.2"/>
    <path d="M8.37357 24.0106V22.6847C8.37357 21.5318 9.30407 20.3643 10.6939 20.3643C11.1996 20.3643 11.4485 20.238 11.5459 20.1617C11.6326 20.0937 11.6262 20.0513 11.6262 20.0497C11.6259 20.0376 11.6195 19.9731 11.5207 19.8924C11.4219 19.8118 11.2061 19.7014 10.8143 19.7014H8.82288C8.09346 19.7013 7.45577 19.4514 7.00428 18.9808C6.56335 18.5211 6.37121 17.9243 6.38535 17.3564C6.41429 16.2003 7.3093 15.0608 8.82288 15.0607C9.58869 15.0607 9.67343 14.8628 9.67812 14.8554C9.70795 14.8088 9.75069 14.7113 9.78041 14.511C9.81049 14.3083 9.81991 14.059 9.81991 13.7348V10.7515C9.81991 10.2023 10.2651 9.75703 10.8143 9.75703C11.3634 9.75715 11.8088 10.2023 11.8088 10.7515V13.7348C11.8088 14.3537 11.7993 15.2323 11.353 15.9289C10.8267 16.7502 9.91535 17.0495 8.82288 17.0495C8.61017 17.0496 8.51518 17.1186 8.47069 17.1641C8.41431 17.2219 8.37606 17.3092 8.37357 17.4056C8.3712 17.5006 8.40365 17.5669 8.43961 17.6044C8.46543 17.6312 8.55782 17.7125 8.82288 17.7125H10.8143C12.3375 17.7126 13.5915 18.6464 13.6151 20.016C13.6264 20.6773 13.3203 21.298 12.7728 21.7271C12.2358 22.148 11.5156 22.3532 10.6939 22.3532C10.5979 22.3532 10.5227 22.3897 10.4621 22.4542C10.3932 22.5276 10.3624 22.6186 10.3624 22.6847V24.0106C10.3624 24.5598 9.91722 25.005 9.36801 25.005C8.8188 25.005 8.37357 24.5598 8.37357 24.0106Z" fill="#281706"/>
    <ellipse cx="11.3106" cy="10.7895" rx="0.207867" ry="0.289754" fill="white"/>
    <ellipse cx="10.3688" cy="10.7895" rx="0.207867" ry="0.289754" fill="white"/>
    <ellipse cx="11.3106" cy="10.6918" rx="0.207867" ry="0.192078" fill="black"/>
    <ellipse cx="10.3688" cy="10.6918" rx="0.207867" ry="0.192078" fill="black"/>`,
    },

    // ── header: sits on top of the card ──────────────────────────────────
    // A drawn header stretches to the card's width (preserveAspectRatio =
    // "none"), so it's authored wide and shallow and avoids round detail that
    // would visibly distort.
    //
    // Both live headers (High Noon, Blockworld) are built from repeating
    // gradients instead — a `rail` — because that repeats evenly at any card
    // width where a drawing can only stretch. `plank` is kept as the worked
    // example for the drawn path `themeHeader()` still supports; without it
    // that branch has nothing to exercise it and SPRITES.md documents a shape
    // nobody can see.
    plank: {
        vb: '0 0 200 34',
        svg: `<rect x="10" y="10" width="180" height="24" rx="2"/>
    <rect x="10" y="14" width="180" height="1.6" fill="#000" fill-opacity=".16"/>
    <rect x="10" y="27" width="180" height="1.6" fill="#000" fill-opacity=".16"/>
    <circle cx="20" cy="22" r="2.2" fill="#000" fill-opacity=".3"/>
    <circle cx="180" cy="22" r="2.2" fill="#000" fill-opacity=".3"/>`,
    },
})

/** One sprite as a standalone `<svg>` string, tinted. */
export function spriteSvg(name, color, attrs = '') {
    const sprite = SPRITES[name]
    if (!sprite) return ''
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${sprite.vb}" ${attrs}>` +
        `<g fill="${color}">${sprite.svg}</g></svg>`
}

/** A sprite's aspect, for sizing one axis from the other. */
export function spriteAspect(name) {
    const sprite = SPRITES[name]
    if (!sprite) return 1
    const [, , w, h] = sprite.vb.split(' ').map(Number)
    return h / w
}

/**
 * Composition primitives — the CSS a scene's background is built from.
 *
 * All of these produce *hard* edges, which is the whole point. The themes
 * these replaced were soft blends, and a soft blend reads as a colour ramp
 * rather than a place no matter what is layered on top of it.
 */

/**
 * Every primitive returns `{ css, spec }` rather than a bare string.
 *
 * The CSS is what the panel paints. The spec is the same layer described in
 * numbers, so something that isn't a browser can draw it too — the shop
 * preview generator rasterises these on a canvas, and parsing the CSS back
 * out is exactly what made the old generator brittle enough to throw on half
 * the catalogue.
 *
 * They used to return the string, and the change to an object broke three
 * separate call sites that kept treating them as one — Cyberpunk and
 * Kaleidoscope concatenated two into a background, and ThemeScene assigned
 * one straight to `backgroundImage`. All three produced the literal text
 * `[object Object]`, which CSS discards in silence: no error, no warning,
 * just a layer that never appears. Two of them shipped that way.
 *
 * Hence `toString`. Used as a string, a layer *is* its CSS, so a call site
 * that hasn't been updated keeps working instead of failing invisibly.
 * Prefer `.css` explicitly where you mean the CSS — this is a safety net,
 * not the interface.
 */
function layer(css, spec) {
    return { css, spec, toString: () => css }
}

/** Hard horizon: N bands with no blending between them. */
export function bands(stops) {
    const parts = []
    stops.forEach(([color, at], i) => {
        parts.push(`${color} ${i === 0 ? 0 : stops[i - 1][1]}%`, `${color} ${at}%`)
    })
    return layer(`linear-gradient(180deg, ${parts.join(',')})`, { kind: 'bands', stops })
}

/** A hard-edged disc — a sun or a moon, rather than a soft glow. */
export function disc(color, x, y, r) {
    return layer(
        `radial-gradient(circle at ${x}% ${y}%, ${color} 0 ${r}%, transparent ${r}%)`,
        { kind: 'disc', color, x, y, r })
}

/** Sunburst rays. */
export function rays(color, count, x = 50, y = 42) {
    return layer(
        `repeating-conic-gradient(from 0deg at ${x}% ${y}%, ` +
        `${color} 0deg ${180 / count}deg, transparent ${180 / count}deg ${360 / count}deg)`,
        { kind: 'rays', color, count, x, y })
}

/** Scanlines / awning stripes. */
export function stripes(color, width, angle = 0) {
    return layer(
        `repeating-linear-gradient(${angle}deg, ${color} 0 ${width}px, transparent ${width}px ${width * 2}px)`,
        { kind: 'stripes', color, width, angle })
}

/** Blueprint grid. */
export function grid(color, cell, weight = 1) {
    return layer(
        `repeating-linear-gradient(0deg, ${color} 0 ${weight}px, transparent ${weight}px ${cell}px),` +
        `repeating-linear-gradient(90deg, ${color} 0 ${weight}px, transparent ${weight}px ${cell}px)`,
        { kind: 'grid', color, cell, weight })
}

/** A non-tiling silhouette anchored to the bottom — mountains, skyline, trees. */
export function ridge(path, color, height = 42) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="${height}" ` +
        `viewBox="0 0 400 ${height}" preserveAspectRatio="none"><path d="${path}" fill="${color}"/></svg>`
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

/** Ridge outlines, as `d` attributes for `ridge()`. */
export const RIDGES = Object.freeze({
    city: 'M0 42V26h18v-8h14v8h10V14h16v12h12v-6h18v6h14V20h16v6h12v-9h14v9h16v-6h14v6h12V16h18v10h14v-6h16v6h12v-4h18v4h14v-8h14v8h16v-5h14v5h12v-7h16v7h14v-4h14v4V42Z',
    pine: 'M0 42 L14 18 L22 30 L34 8 L48 30 L58 16 L70 30 L84 10 L98 30 L108 20 L122 30 L136 6 L150 30 L160 18 L174 30 L188 12 L202 30 L212 22 L226 30 L240 8 L254 30 L264 18 L278 30 L292 10 L306 30 L316 24 L330 30 L344 12 L358 30 L368 20 L382 30 L400 16 L400 42 Z',
    dune: 'M0 42 L0 30 C60 12 110 34 170 22 C230 10 280 30 340 20 C365 16 385 22 400 18 L400 42 Z',
})

/**
 * A tiny deterministic PRNG, seeded from a string.
 *
 * Not for anything that matters — it exists so a scene lays out the same way
 * every time. Two reasons: the arrangement is stable across a re-render (a
 * resize would otherwise reshuffle every leaf and relocate every animal), and
 * shop artwork generated from a theme matches what a player actually gets.
 */
export function seeded(key) {
    let state = 0
    for (let i = 0; i < key.length; i++) state = (state * 31 + key.charCodeAt(i)) % 2147483647
    state = state || 7
    return () => {
        state = (state * 1103515245 + 12345) % 2147483648
        return state / 2147483648
    }
}

/**
 * Places a theme's cast of peekers into one zone.
 *
 * ── Why a theme doesn't list its peekers ─────────────────────────────────
 *
 * They used to be written out one by one — sprite, zone, width, left, delay,
 * duration — which meant every animal was a hand-tuned coordinate, adding a
 * card meant editing the theme, and moving one meant re-picking numbers so it
 * didn't collide or fall into step with its neighbours.
 *
 * A theme now says *who's in it* and roughly how many, and placement is
 * derived here. A zone is just a mount point: put a <peek-slot> anywhere and
 * it populates itself, for every theme that has a cast, with no theme change
 * at all.
 *
 * Seeded by the zone name, so each zone gets its own stable arrangement —
 * the same on every render, different from its neighbours, and never the
 * evenly-spaced look that gives away a loop.
 *
 * @param {object} spec  A theme's `peek`: { cast, colors, zones, perZone, width }.
 * @param {string} zone  Where they're being placed. 'horizon' is a zone too.
 */
export function castPeekers(spec, zone) {
    if (!spec || !Array.isArray(spec.cast) || !spec.cast.length) return []
    // An explicit list restricts; without one, a cast turns up anywhere a slot
    // is mounted.
    if (Array.isArray(spec.zones) && !spec.zones.includes(zone)) return []

    const random = seeded(`${zone}|${spec.cast.join(',')}`)
    const count = Math.max(1, Math.min(spec.perZone || 2, 6))
    const [minWidth, maxWidth] = spec.width || [28, 40]

    // Shuffled rather than picked at random each time: drawing independently
    // put two of the same animal in one zone, side by side, which reads as a
    // bug rather than a coincidence. Walking a shuffled cast gives distinct
    // neighbours until it runs out.
    const shuffle = list => {
        const out = list.slice()
        for (let i = out.length - 1; i > 0; i--) {
            const j = Math.floor(random() * (i + 1))
            const swap = out[i]
            out[i] = out[j]
            out[j] = swap
        }
        return out
    }

    const cast = shuffle(spec.cast)
    // The palette is shuffled per zone too, not just indexed. Walking it in
    // order meant `perZone: 2` could only ever reach the first two colours —
    // the arcade's third invader colour existed and never once appeared.
    const colors = spec.colors ? shuffle(spec.colors) : null

    // One per band across the usable span, so two peekers in a zone can't
    // land on top of each other. The edges are left clear — a sprite there
    // would be half cut off by the card's own rounding.
    const SPAN_START = 6
    const SPAN = 80
    const band = SPAN / count

    const out = []
    for (let i = 0; i < count; i++) {
        out.push({
            sprite: cast[i % cast.length],
            // Optional palette, one per instance. The arcade's invaders were
            // three colours before the cast API flattened them to one, and
            // the variety was most of why they read as a row of invaders
            // rather than one sprite repeated.
            color: colors ? colors[i % colors.length] : spec.color,
            width: Math.round(minWidth + random() * (maxWidth - minWidth)),
            left: `${(SPAN_START + band * i + random() * band * 0.55).toFixed(1)}%`,
            // Durations are spread and deliberately not multiples of each
            // other, so a row of them never falls into step. In unison they
            // read as a mechanism; staggered, as a farm.
            delay: +(random() * 16).toFixed(1),
            seconds: +(11 + random() * 9).toFixed(1),
        })
    }
    return out
}
